/**
 * Pictures in and out of a document: the one decode-draw-encode routine the
 * upload (compressImage, contain) and the export (resampleForPdf, cover)
 * both run, so a fix to either -- the white fill, the smoothing, an
 * orientation rule -- reaches both.
 *
 * Why the export resamples at all: react-pdf embeds a JPEG's bytes as they
 * are (it has no quality or downsampling option), and the pdf-lib merge
 * afterwards copies streams untouched. A photo is stored at up to 1920px on
 * its long side (the upload cap, which the editor still wants for zooming),
 * while a two-column appendix cell is 238pt wide: the stored pixels land at
 * over 400 ppi, and a 27-photo report came out at 26 MB. The only place the
 * size can come down is between the download and the data URL react-pdf is
 * given. See "Photos are resampled at export" in DEVELOPMENT.md.
 */

/** Pixels per inch a photo is embedded at, measured against its frame. */
export const EXPORT_PHOTO_PPI = 200;

/** JPEG quality of a resampled photo. */
export const EXPORT_JPEG_QUALITY = 0.8;

/** The box a photo covers on the page, in points. */
export interface FramePt {
  widthPt: number;
  heightPt: number;
}

/** The decoded picture, or null if the browser cannot decode it. */
export const decodeImage = (blob: Blob): Promise<HTMLImageElement | null> =>
  new Promise(resolve => {
    const url = URL.createObjectURL(blob);
    const img = new window.Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img.naturalWidth > 0 && img.naturalHeight > 0 ? img : null);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });

/** A picture's pixel size, or null if the browser cannot decode it. */
export const readImageSize = async (blob: Blob): Promise<{ width: number; height: number } | null> => {
  const img = await decodeImage(blob);
  return img ? { width: img.naturalWidth, height: img.naturalHeight } : null;
};

/**
 * Draw a decoded picture at `width` x `height` and encode it as a JPEG.
 * The canvas is filled white first: a picture with transparency (a PNG the
 * upload could not convert) would otherwise come out on black, since JPEG
 * has no alpha. Null if the browser could not encode it.
 */
export const drawToJpeg = (
  img: HTMLImageElement,
  width: number,
  height: number,
  quality: number
): Promise<Blob | null> => {
  const canvas = window.document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  const context = canvas.getContext('2d');
  if (!context) return Promise.resolve(null);
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(img, 0, 0, canvas.width, canvas.height);
  return new Promise(resolve => {
    canvas.toBlob(blob => resolve(blob), 'image/jpeg', quality);
  });
};

/**
 * A picture scaled to FIT within maxWidth x maxHeight (contain) as a JPEG:
 * what every upload goes through. Throws when the picture cannot be decoded
 * or encoded; callers fall back to uploading the file as it is.
 */
export const compressImage = async (
  file: Blob,
  options: { maxWidth?: number; maxHeight?: number; quality?: number } = {}
): Promise<Blob> => {
  const { maxWidth = 1920, maxHeight = 1920, quality = 0.85 } = options;
  const img = await decodeImage(file);
  if (!img) throw new Error('Failed to load image');
  const scale = Math.min(1, maxWidth / img.naturalWidth, maxHeight / img.naturalHeight);
  const out = await drawToJpeg(img, img.naturalWidth * scale, img.naturalHeight * scale, quality);
  if (!out) throw new Error('Failed to compress image');
  return out;
};

/**
 * The stored picture, or a smaller JPEG of it when it has more pixels than
 * its frames need at `ppi`. Both surfaces draw pictures with object-fit:
 * cover, so for each frame the LARGER of the two frame/image ratios is the
 * scale actually drawn, and a picture placed in several frames is sized for
 * the largest such scale -- not the largest frame, which a narrow one can
 * out-demand. Never scaled up. With no frames (a plan page, whose line work
 * keeps its rasterised size) the picture is returned as it is, as is one
 * the browser cannot decode, and a re-encode that came out no smaller.
 */
export const resampleForPdf = async (
  blob: Blob,
  frames: FramePt[],
  ppi = EXPORT_PHOTO_PPI,
  quality = EXPORT_JPEG_QUALITY
): Promise<Blob> => {
  const usable = frames.filter(f => f.widthPt > 0 && f.heightPt > 0);
  if (usable.length === 0) return blob;

  const img = await decodeImage(blob);
  if (!img) return blob;

  const scale = Math.max(...usable.map(f => Math.max(
    ((f.widthPt / 72) * ppi) / img.naturalWidth,
    ((f.heightPt / 72) * ppi) / img.naturalHeight
  )));
  if (scale >= 1) return blob;

  const out = await drawToJpeg(img, img.naturalWidth * scale, img.naturalHeight * scale, quality);
  return out && out.size < blob.size ? out : blob;
};

export const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
